import React from "react";
import { alpha, Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatFinance } from "../../../utils/formatters";

/**
 * Component displaying weekly cost data visualization
 * @param {Object} props - Component props
 * @param {Array} props.data - Weekly chart data
 * @param {string} props.chartType - Chart type (bar, line, pie)
 * @param {Array} props.chartColors - Array of colors for the chart
 * @param {Function} props.renderCustomizedPieLabel - Custom label renderer for pie chart
 * @returns {JSX.Element} Weekly chart component
 */
const WeeklyChart = ({
  data,
  chartType,
  chartColors,
  renderCustomizedPieLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Custom tooltip formatter that translates the key name
  const customTooltipFormatter = (value, name) => {
    // Change the display name from "value" to "Valor"
    const displayName = name === "value" ? "Valor" : name;
    return [formatFinance(value / 100), displayName];
  };

  // Legend formatter to translate the key name
  const legendFormatter = (value) => {
    return value === "value" ? "Valor" : value;
  };

  // If no data available, show message
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          Nenhum dado semanal disponível
        </Typography>
      </Box>
    );
  }

  // Render appropriate chart based on chartType
  return (
    <Box sx={{ width: "100%", height: 350 }}>
      <ResponsiveContainer width="100%" height="100%">
        {chartType === "pie" ? (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedPieLabel}
              outerRadius={isMobile ? 100 : 150}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={chartColors[index % chartColors.length]}
                />
              ))}
            </Pie>
            <RechartsTooltip formatter={customTooltipFormatter} />
            <Legend formatter={legendFormatter} />
          </PieChart>
        ) : chartType === "line" ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(value) => formatFinance(value / 100)} />
            <RechartsTooltip formatter={customTooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={{ fill: theme.palette.primary.main }}
              activeDot={{ r: 8 }}
              name="Valor" // Set display name directly for Line
            />
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(value) => formatFinance(value / 100)} />
            <RechartsTooltip formatter={customTooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Bar
              dataKey="value"
              fill={theme.palette.primary.main}
              animationDuration={1000}
              name="Valor" // Set display name directly for Bar
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={alpha(
                    theme.palette.primary.main,
                    (index + 5) / (data.length + 4)
                  )}
                />
              ))}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

export default WeeklyChart;
